export enum TaskType {
  MANUAL = "MANUAL",
  AUTOMATED = "AUTOMATED",
  SECTION = "SECTION",
}

export enum TaskStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  WAITING_FOR_USER = "WAITING_FOR_USER",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  SKIPPED = "SKIPPED",
}

export interface Organisation {
  id: string;
  name: string;
  slug: string;
  type?: "PERSONAL" | "SHARED";
  role?: string; // Current user's role
  ownerId?: string;
  memberIds?: string[];
  githubInstallationId?: string;
  subscriptionStatus?:
    | "FREE"
    | "TRIALING"
    | "TEAM"
    | "ENTERPRISE"
    | "ACTIVE"
    | "PAST_DUE"
    | "CANCELED";
  subscriptionCurrentPeriodEnd?: number;
  stripePriceId?: string;
  tier?: "FREE" | "TEAM" | "ENTERPRISE";
}

export interface User {
  id: string;
  name: string;
  email: string;
  initials: string;
  role?: string;
  imageUrl?: string;
  organisationIds?: string[];
}

export interface TeamMember {
  userId: string;
  role: string;
}

export interface Team {
  id: string;
  organisationId: string;
  name: string;
  members: TeamMember[];
  // Deprecated but maybe useful for quick lookups
  memberIds?: string[];
}

export interface Invitation {
  id: string;
  email: string;
  organisationId: string;
  organisationName: string;
  organisationSlug?: string;
  role: string;
  status: "PENDING" | "ACCEPTED" | "DECLINED";
  createdAt: number;
  expiresAt: number;
}

export interface TaskDefinition {
  id: string;
  name: string;
  description: string;
  type: TaskType;
  durationMinutes: number;
  assignees?: { type: "USER" | "TEAM"; id: string }[]; // New: Multiple users/teams
  automationPrompt?: string; // For automated tasks
  githubRepo?: string; // For automated tasks (owner/repo)
  githubWorkflow?: string; // For automated tasks (filename.yml)
  githubBranch?: string; // For automated tasks (branch name)
  githubInputs?: Record<string, any>; // For automated tasks (input values)
  dependencyIds: string[];
  schedulingStrategy?: "AEAP" | "ALAP";
  durationStrategy?: "ESTIMATED_DURATION" | "FIXED_END_TIME";
  fixedEndTime?: {
    time: string; // HH:mm format
    daysOffset: number; // 0 for same day, 1 for next day, etc.
  };
}

export type PermissionLevel = "VIEWER" | "EDITOR";

export interface PlanShare {
  id: string;
  planId: string;
  granteeType: "USER" | "TEAM" | "ORGANISATION";
  granteeId: string;
  permission: PermissionLevel;
}

export interface TaskPlan {
  id: string;
  organisationId: string;
  name: string;
  description: string;
  createdAt: number;
  tasks?: TaskDefinition[];
  ownerId?: string;
  shares?: PlanShare[];
  userPermission?: "VIEWER" | "EDITOR" | "OWNER";
  taskCount?: number;
  totalDuration?: number;
  estimatedStartTime?: number; // Optional estimated start time for planning
  prerequisites?: string[]; // Checklist items to complete before starting
}

export interface TaskRunState {
  status: TaskStatus;
  startTime?: number;
  endTime?: number;
  logs: string[];
  output?: string;
  error?: string;
  githubRunId?: string;
  notes?: string;
}

export type PlanRunStatus = "RUNNING" | "COMPLETED" | "FAILED" | "SCHEDULED";

export interface PlanRun {
  id: string;
  organisationId: string;
  planId: string;
  name: string;
  planSnapshot?: TaskPlan; // Store a copy of the plan as it was when run started
  startTime: number; // When execution actually starts
  scheduledStartTime?: number; // When it was scheduled to start
  endTime?: number;
  status: PlanRunStatus;
  taskStates?: Record<string, TaskRunState>;
  resolvedUsers?: {
    id: string;
    name: string;
    initials: string;
    imageUrl?: string;
  }[];
}

export interface NotificationTask {
  type: "TASK";
  id: string; // Task Run ID
  taskId: string;
  taskName: string;
  planRunId: string;
  planRunName: string;
  organisationId: string;
  organisationName: string;
  organisationSlug: string;
  startTime: number;
}

export interface NotificationInvitation {
  type: "INVITATION";
  id: string;
  organisationId: string;
  organisationName: string;
  organisationSlug: string;
  role: string;
  createdAt: number;
}

export interface NotificationsResponse {
  invitations: NotificationInvitation[];
  tasks: NotificationTask[];
}
